import { useSortable } from '@dnd-kit/sortable';
import { type ComponentProps, memo, type ReactNode, useMemo } from 'react';

import { Eye, EyeClosed, Gizmo } from '@air/next-icons';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

import { VISIBILITY_TOGGLE } from '~/constants/testIDs';
import { type ConfigViewOption } from '~/store/configViews/types';
import { getSortableItemStyles } from '~/utils/configurableViewFieldsUtils';

export type ViewVisibilityToggleSortableProps = Pick<ComponentProps<'button'>, 'onClick'> & {
  option: ConfigViewOption;
  prefix?: ReactNode;
  isDraggable?: boolean;
  isDragging?: boolean;
};

export const ViewVisibilityToggleSortable = memo(
  ({ option, prefix, isDraggable, isDragging, ...rest }: ViewVisibilityToggleSortableProps) => {
    const sortableProps = useSortable({
      id: option.derivedId,
      disabled: !isDraggable,
    });

    const style = useMemo(() => getSortableItemStyles(sortableProps), [sortableProps]);

    const renderContent = () => (
      <button
        className={cn(isDraggable && 'w-[calc(100%-1.5rem)]', isDraggable && isDragging && 'pointer-events-none')}
        data-testid={VISIBILITY_TOGGLE}
        data-field-name={option.fieldName}
        {...rest}
      >
        {prefix ? <div className="mr-2 shrink-0">{prefix}</div> : null}

        <div className="flex w-full items-center justify-between">
          <div className="min-w-0 truncate font-normal text-14 text-grey-12 leading-5 tracking-[-0.084px]">
            {option.label}
          </div>
          <div className="ml-2 shrink-0 text-grey-10">
            {option.isVisible ? (
              <Eye data-testid={`${option.fieldName}-visible`} width={16} />
            ) : (
              <EyeClosed data-testid={`${option.fieldName}-not-visible`} width={16} />
            )}
          </div>
        </div>
      </button>
    );

    if (!isDraggable) {
      return renderContent();
    }

    return (
      <div
        ref={sortableProps?.setNodeRef}
        style={style}
        className={cn('flex items-center gap-2 py-2 pr-1.5', isDragging && 'cursor-grabbing')}
        {...sortableProps?.attributes}
      >
        <IconButton
          className={cn('shrink-0', isDragging && 'cursor-grabbing')}
          icon={Gizmo}
          appearance="ghost"
          color="grey"
          size="small"
          label="Move"
          {...sortableProps?.listeners}
        />
        {renderContent()}
      </div>
    );
  },
);

ViewVisibilityToggleSortable.displayName = 'ViewVisibilityToggleSortable';
